import { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { ShieldAlert, ChevronRight } from 'lucide-react';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger } from './ui/popover';

// Sino de pendências de governança — mostra em tempo real quais profissionais
// estão com a agenda BLOQUEADA por pendências críticas. Orienta a Recepção a
// não oferecer horário desses profissionais antes mesmo de tentar agendar
// (evita fricção de bater no HTTP 423 do POST /agendamentos).
//
// Visível apenas para Recepção e Administrador. Poll de 120s (dado muda pouco
// durante o dia) + pausa quando aba oculta + refetch imediato ao ganhar foco.
const BloqueadosBell = () => {
  const { authFetch, user } = useAuth();
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);

  const isElegivel = user?.perfil === 'recepcao' || user?.perfil === 'administrador';

  const fetchStatus = useCallback(async () => {
    if (!isElegivel) return;
    if (document.hidden) return;
    try {
      const resp = await authFetch('/governanca/bloqueados-resumo', {
        suppressAuthRedirect: true,
        timeout: 60000,
      });
      const lista = (resp.data?.bloqueados || []).map(p => ({
        id: p.profissional_id,
        nome: (p.nome || '?').trim(),
        especialidade: p.especialidade || '',
        total: p.total_critico || 0,
        maisAntigoDias: p.mais_antigo_dias || 0,
      }));
      setItems(lista);
    } catch (err) {
      // silencioso: sino é acessório
      console.warn('BloqueadosBell fetch falhou', err?.message || err);
    }
  }, [authFetch, isElegivel]);

  useEffect(() => {
    if (!isElegivel) return;
    fetchStatus();
    const interval = setInterval(fetchStatus, 300000); // 5 min (mesmo TTL do cache backend)
    const onVisChange = () => { if (!document.hidden) fetchStatus(); };
    document.addEventListener('visibilitychange', onVisChange);
    window.addEventListener('focus', fetchStatus);
    return () => {
      clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisChange);
      window.removeEventListener('focus', fetchStatus);
    };
  }, [fetchStatus, isElegivel]);

  const total = items.length;
  const tooltip = useMemo(() => (
    total === 0
      ? 'Nenhum profissional bloqueado'
      : `${total} profissional(is) com agenda bloqueada`
  ), [total]);

  if (!isElegivel) return null;

  const isAdmin = user?.perfil === 'administrador';

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          title={tooltip}
          data-testid="bloqueados-bell"
        >
          <ShieldAlert
            className="h-5 w-5"
            style={{ color: total > 0 ? '#DC2626' : '#71717A' }}
          />
          {total > 0 && (
            <span
              className="absolute -top-1 -right-1 h-5 w-5 rounded-full bg-red-600 text-white text-xs flex items-center justify-center font-bold animate-pulse"
              data-testid="bloqueados-bell-count"
            >
              {total > 9 ? '9+' : total}
            </span>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        className="w-80 p-0"
        data-testid="bloqueados-bell-popover"
      >
        <div className="p-3 border-b" style={{ borderColor: '#E2E4E9' }}>
          <div className="flex items-center gap-2">
            <ShieldAlert className="h-4 w-4" style={{ color: '#DC2626' }} />
            <h3 className="text-sm font-semibold" style={{ color: '#18181B' }}>
              Agendas Bloqueadas
            </h3>
          </div>
          <p className="text-xs mt-1" style={{ color: '#71717A' }}>
            {total === 0
              ? 'Nenhum profissional com agenda bloqueada no momento.'
              : 'Não oferecer horário destes profissionais até regularizar.'}
          </p>
        </div>

        <div className="max-h-80 overflow-y-auto" data-testid="bloqueados-bell-list">
          {total === 0 ? (
            <div className="p-4 text-center text-sm" style={{ color: '#A1A1AA' }}>
              Tudo em ordem.
            </div>
          ) : (
            items.map(item => (
              <button
                key={item.id}
                type="button"
                onClick={() => {
                  if (isAdmin) {
                    navigate('/dashboard/governanca');
                    setOpen(false);
                  }
                }}
                disabled={!isAdmin}
                className={`w-full text-left px-3 py-2.5 border-b flex items-start gap-2 transition-colors ${isAdmin ? 'hover:bg-red-50 cursor-pointer' : 'cursor-default'}`}
                style={{ borderColor: '#F4F4F5' }}
                data-testid={`bloqueado-item-${item.id}`}
              >
                <div className="flex-1 min-w-0">
                  <div className="text-sm font-medium truncate" style={{ color: '#18181B' }}>
                    {item.nome}
                  </div>
                  <div className="text-xs mt-0.5" style={{ color: '#71717A' }}>
                    {item.especialidade}
                  </div>
                  <div className="text-xs mt-1" style={{ color: '#DC2626' }}>
                    {item.total} pendência(s) crítica(s)
                    {item.maisAntigoDias > 0 && ` • mais antiga: ${item.maisAntigoDias}d`}
                  </div>
                </div>
                {isAdmin && (
                  <ChevronRight className="h-4 w-4 flex-shrink-0 mt-0.5" style={{ color: '#A1A1AA' }} />
                )}
              </button>
            ))
          )}
        </div>

        {isAdmin && total > 0 && (
          <div className="p-2 border-t" style={{ borderColor: '#E2E4E9' }}>
            <Button
              variant="ghost"
              size="sm"
              className="w-full text-xs"
              onClick={() => { navigate('/dashboard/governanca'); setOpen(false); }}
              data-testid="bloqueados-bell-ir-governanca"
            >
              Ir para Governança
            </Button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
};

export default BloqueadosBell;
